export const VERTICALS = {
  healthcare: {
    path: '/healthcare',
    slug: 'healthcare',
    seo: {
      title: 'Aadhaar Patient Verification for Clinics & Hospitals',
      description:
        'Replace manual patient ID checks with Aadhaar-verified digital onboarding. DPDP compliant, zero paperwork, audit-ready. Free pilot for clinics and hospitals in India.',
      keywords:
        'hospital patient verification India, clinic Aadhaar check-in, patient onboarding KYC, DPDP compliant healthcare, digital patient register India',
      url: 'https://idgate.in/healthcare',
    },
    structuredDataName: 'IDGate — Clinic & Hospital Patient Verification',
    structuredDataDescription:
      'Replace manual patient ID checks with Aadhaar-verified digital onboarding for clinics and hospitals. DPDP compliant, audit-ready.',
    hero: {
      eyebrow: 'For Clinics & Hospitals',
      h1: 'Patient identity errors\nhave real consequences.',
      sub: "IDGate verifies every patient's identity against UIDAI before they reach your desk. No paper. No manual ID check. No insurance fraud from wrong-patient records.",
    },
    painPoints: {
      h2: 'What goes wrong with manual patient ID',
      sub: 'Every one of these has caused insurance rejections or compliance notices.',
      items: [
        'Patients registered under wrong names or DOBs',
        'Insurance claims rejected due to ID mismatch',
        'Paper forms with illegible handwriting',
        'No consent trail for health data collected under DPDP',
        'Staff photocopying Aadhaar cards — a DPDP violation',
        'No way to export patient records for an audit date range',
      ],
    },
    steps: [
      {
        title: 'Patient scans QR at reception',
        body: 'One QR code at your OPD counter or reception. Works on any smartphone with mAadhaar installed. No app for staff.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Identity confirmed in under 60 seconds directly by UIDAI. No photocopy. No manual check. Consent captured digitally.',
      },
      {
        title: 'Record synced to your HMS or Google Sheet',
        body: 'Verified patient record delivered to your Hospital Management System or Google Sheet via webhook. Audit-ready instantly.',
      },
    ],
    benefits: [
      'Aadhaar-verified identity for every patient',
      'Zero insurance mismatches from identity errors',
      'DPDP compliant — consent captured at every check-in',
      'No staff photocopying Aadhaar (DPDP violation eliminated)',
      'Exportable patient log for insurance and compliance audits',
      'Google Sheet or HMS integration via webhook',
      'OPD queue time reduced — check-in under 60 seconds',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from clinic and hospital administrators',
      items: [
        {
          q: 'Does IDGate store patient Aadhaar data?',
          a: "No. IDGate is DPDP compliant. Verification is handled directly by UIDAI. Your clinic receives only the consent-backed record — name, timestamp, and verification status. Aadhaar numbers are never stored.",
        },
        {
          q: 'How does IDGate help with DPDP compliance in a clinical setting?',
          a: "IDGate captures explicit digital consent from each patient before verification. This satisfies the consent requirements under India's Digital Personal Data Protection Act. Staff never handle physical Aadhaar cards, eliminating one of the most common DPDP violations in clinics.",
        },
        {
          q: 'Can IDGate integrate with our Hospital Management System?',
          a: 'Yes. IDGate delivers verified patient records via webhook or to a Google Sheet. Most HMS platforms (Practo, eHospital, Insta HMS) can receive webhook data or import from Sheets.',
        },
      ],
    },
    onlineUses: [
      'Send a verification link in your appointment confirmation — patients arrive pre-verified',
      'Embed in your telemedicine portal to verify patient identity before the consultation',
      'Add to your WhatsApp booking flow so OPD queues move faster',
    ],
    cta: {
      h2: 'Make every patient record audit-proof this week.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your clinic',
    },
  },

  corporate: {
    path: '/corporate',
    slug: 'corporate',
    seo: {
      title: 'Aadhaar Visitor Management for Corporate Offices',
      description:
        'Replace paper visitor registers at your corporate office with Aadhaar-verified digital check-in. DPDP compliant, audit-ready. Free pilot for enterprises in India.',
      keywords:
        'corporate visitor management India, office visitor check-in Aadhaar, enterprise visitor log India, DPDP compliant visitor management, contractor verification India',
      url: 'https://idgate.in/corporate',
    },
    structuredDataName: 'IDGate — Corporate Office Visitor Management',
    structuredDataDescription:
      'Replace paper visitor registers at corporate offices with Aadhaar-verified digital check-in. DPDP compliant, contractor-ready, audit-ready.',
    hero: {
      eyebrow: 'For Corporate Offices',
      h1: 'You know who badged in.\nBut do you know who they are?',
      sub: "IDGate gives your security team Aadhaar-verified entry records for every visitor and contractor — with a digital audit trail that holds up in any incident investigation.",
    },
    painPoints: {
      h2: 'What breaks in a manual visitor system',
      sub: 'Most corporate security audits flag at least three of these.',
      items: [
        'Contractors entering without verified identity',
        'Visitor register with fake names and phone numbers',
        'No way to produce an entry log for a specific date range',
        'No consent trail for visitor data collected',
        'Security staff manually checking IDs — inconsistent and slow',
        'Post-incident investigation with no reliable entry record',
      ],
    },
    steps: [
      {
        title: 'Visitor scans QR at lobby',
        body: 'One QR code at your reception or security desk. Works on any smartphone with mAadhaar. No app for security staff.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Identity confirmed in under 60 seconds by UIDAI. No photocopy. No manual check. Every verification is logged with timestamp and consent.',
      },
      {
        title: 'Record synced to your access system',
        body: 'Verified visitor record sent to your Google Sheet or access control system via webhook. Exportable for any audit.',
      },
    ],
    benefits: [
      'Aadhaar-verified entry for every visitor and contractor',
      'Audit-ready entry log for security investigations',
      'DPDP compliant — consent captured at every check-in',
      'Contractor compliance — verified before they step inside',
      'Google Sheet or access control integration via webhook',
      'Security staff do not touch visitor IDs',
      'ISO 27001 and enterprise security audit-ready',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from corporate security and facility teams',
      items: [
        {
          q: 'Can IDGate distinguish between employee, visitor, and contractor check-ins?',
          a: 'Yes. IDGate supports separate QR flows for different visitor types. Each flow generates a labelled, Aadhaar-verified record so your log is always segmented and audit-ready.',
        },
        {
          q: 'How does IDGate support post-incident investigations?',
          a: 'Every IDGate check-in creates an immutable, timestamped, Aadhaar-verified record. In the event of a security incident, you can export the full entry log for any date range in under 30 seconds.',
        },
        {
          q: 'Does IDGate integrate with our access control system?',
          a: 'IDGate delivers records via webhook in real time. Most enterprise access control systems (HID, Honeywell, Suprema) can receive webhook events or import from Google Sheets.',
        },
      ],
    },
    onlineUses: [
      'Send a pre-registration link to visitors before their meeting — lobby cleared before they arrive',
      'Embed in your contractor onboarding portal for identity verification before site access is granted',
      'Add to your job candidate pre-screening form to verify applicant identity online',
    ],
    cta: {
      h2: 'Make your office security audit-proof this week.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your office',
    },
  },

  events: {
    path: '/events',
    slug: 'events',
    seo: {
      title: 'Aadhaar Attendee Verification for Events & Venues',
      description:
        'Replace manual check-in and fake tickets with Aadhaar-verified attendee registration. DPDP compliant, fraud-proof. Free pilot for event organizers in India.',
      keywords:
        'event attendee verification India, Aadhaar event check-in, venue entry management India, event registration KYC, ticket fraud prevention India',
      url: 'https://idgate.in/events',
    },
    structuredDataName: 'IDGate — Event & Venue Attendee Verification',
    structuredDataDescription:
      'Replace manual check-in and ticket fraud with Aadhaar-verified attendee registration for events and venues. DPDP compliant, audit-ready.',
    hero: {
      eyebrow: 'For Events & Venues',
      h1: 'Ticket fraud is easier\nthan you think to stop.',
      sub: 'IDGate gives event organizers Aadhaar-verified attendee check-in — no proxies, no fraudulent entries, and a verified attendance list that sponsors actually trust.',
    },
    painPoints: {
      h2: 'What goes wrong with manual event check-in',
      sub: 'These problems compound at every event you run.',
      items: [
        'Fake or transferred tickets letting wrong people in',
        'Proxy delegates at paid conferences and workshops',
        'No verified attendance record for sponsors and CPE credits',
        "Headcount at venue doesn't match registration list",
        'No consent trail for attendee data collected',
        'Post-event lead list with fake names and emails',
      ],
    },
    steps: [
      {
        title: 'Attendee scans QR at entry',
        body: 'One QR code at your event gate or registration desk. Works on any smartphone with mAadhaar installed. No new hardware.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Identity confirmed in under 60 seconds by UIDAI. No paper form. No manual ID check. Consent captured digitally.',
      },
      {
        title: 'Verified attendee record synced to your CRM',
        body: 'Verified attendance record sent to your event CRM or Google Sheet. Exportable for sponsors and CPE certificate issuance.',
      },
    ],
    benefits: [
      'Aadhaar-verified entry — zero proxies or ticket fraud',
      'Verified attendance list for sponsor reporting',
      'CPE credit and certificate issuance with verified names',
      'DPDP compliant — consent captured at check-in',
      'Real-time headcount in your Google Sheet or CRM',
      'No manual ID checking at the gate',
      'Exportable attendee log with timestamps',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from event organizers',
      items: [
        {
          q: 'How does IDGate prevent ticket fraud at events?',
          a: "IDGate ties each entry to a real Aadhaar-verified identity. A ticket cannot be transferred or duplicated because the system verifies who is presenting it, not just that a ticket exists.",
        },
        {
          q: 'Can IDGate handle high attendee volumes at the gate?',
          a: 'Each IDGate verification takes under 60 seconds per attendee. For large events, you can deploy multiple QR codes at different entry points — each generating records to the same shared Google Sheet or CRM.',
        },
        {
          q: 'Can I share the verified attendance list with sponsors?',
          a: 'Yes. IDGate generates an exportable, verified attendance record. Attendee consent is captured at check-in, so the data can be shared with sponsors in compliance with DPDP requirements.',
        },
      ],
    },
    onlineUses: [
      'Send verification links in booking confirmation emails — attendees arrive pre-cleared at the gate',
      'Embed in your event registration page so identity is verified at the time of ticket purchase',
      'Add to your WhatsApp broadcast the day before — faster entry, zero paper at the door',
    ],
    cta: {
      h2: 'Make your next event fraud-proof.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your event',
    },
  },

  banking: {
    path: '/banking',
    slug: 'banking',
    seo: {
      title: 'Aadhaar Branch KYC for Banks & NBFCs',
      description:
        'Replace paper KYC forms at bank branches with Aadhaar-verified digital verification. RBI-ready, DPDP compliant. Free pilot for banks and NBFCs in India.',
      keywords:
        'bank branch KYC India, NBFC customer verification, Aadhaar KYC banking India, digital KYC bank India, DPDP compliant banking, branch onboarding India',
      url: 'https://idgate.in/banking',
    },
    structuredDataName: 'IDGate — Bank & NBFC Branch KYC Verification',
    structuredDataDescription:
      'Replace paper KYC at bank branches with Aadhaar-verified digital verification. RBI-ready, DPDP compliant, audit-ready.',
    hero: {
      eyebrow: 'For Banks & NBFCs',
      h1: "Branch KYC shouldn't mean\nanother paper form.",
      sub: 'IDGate replaces paper KYC at your branch counter with Aadhaar-verified digital onboarding — UIDAI-backed, RBI audit-ready, and done in under 60 seconds per customer.',
    },
    painPoints: {
      h2: 'What fails in branch KYC today',
      sub: 'RBI compliance audits surface all of these.',
      items: [
        'Paper KYC forms with illegible handwriting',
        'Staff manually verifying Aadhaar photocopies — compliance risk',
        'No digital record of walk-in customer identity verification',
        'Long queues when KYC takes 10+ minutes per customer',
        'No consent trail for customer data collected under DPDP',
        'Inability to export KYC records for a date range during audits',
      ],
    },
    steps: [
      {
        title: 'Customer scans QR at the counter',
        body: 'One QR code at your branch counter or customer service desk. Works on any smartphone with mAadhaar installed.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'KYC confirmed in under 60 seconds directly by UIDAI. No paper. No photocopies. No manual check. Consent captured digitally.',
      },
      {
        title: 'KYC record synced to your core banking system',
        body: 'Verified KYC record delivered to your CBS or Google Sheet via webhook. Audit-ready for RBI inspections.',
      },
    ],
    benefits: [
      'Aadhaar-verified KYC for every walk-in customer',
      'RBI audit-ready digital KYC records',
      'DPDP compliant — consent captured at every verification',
      'Staff never touch Aadhaar photocopies — compliance maintained',
      'KYC time reduced to under 60 seconds per customer',
      'CBS or Google Sheet integration via webhook',
      'Exportable KYC log for any date range in under 30 seconds',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from bank branch managers and compliance officers',
      items: [
        {
          q: "Is IDGate's Aadhaar verification accepted for RBI KYC requirements?",
          a: "IDGate uses UIDAI's official Aadhaar authentication. The verified record includes name, demographic data, and a timestamped consent trail — satisfying RBI's digital KYC documentation requirements for walk-in customer onboarding.",
        },
        {
          q: 'How does IDGate handle customer consent under DPDP for banking?',
          a: "IDGate captures explicit digital consent from the customer before any data is verified. The consent record is timestamped and linked to the verification, satisfying India's Digital Personal Data Protection Act requirements for financial institutions.",
        },
        {
          q: 'Can IDGate integrate with our Core Banking System?',
          a: 'IDGate delivers records via webhook in real time. Most CBS platforms (Finacle, BaNCS, Temenos) can receive webhook events or import from Google Sheets. Your IT team can connect the webhook in one afternoon.',
        },
      ],
    },
    onlineUses: [
      'Embed in your account opening form for fully online, RBI-compliant customer KYC',
      'Send a pre-visit KYC link before branch appointments — counter time cut in half',
      'Add to your loan application flow so identity is verified before the officer review',
    ],
    cta: {
      h2: 'Pass your next RBI audit with confidence.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your branch',
    },
  },

  'gated-communities': {
    path: '/gated-communities',
    slug: 'gated_communities',
    seo: {
      title: 'Aadhaar Visitor Management for Gated Communities & Apartments',
      description:
        'Replace paper visitor registers at your society gate with Aadhaar-verified digital check-in. DPDP compliant. Free pilot for housing societies and gated communities in India.',
      keywords:
        'gated community visitor management India, apartment visitor check-in, society gate management Aadhaar, housing society security India, domestic worker verification India',
      url: 'https://idgate.in/gated-communities',
    },
    structuredDataName: 'IDGate — Gated Community & Apartment Visitor Management',
    structuredDataDescription:
      'Replace paper visitor registers at society gates with Aadhaar-verified check-in. DPDP compliant, resident-friendly, audit-ready.',
    hero: {
      eyebrow: 'For Gated Communities & Apartments',
      h1: 'Your security guard is verifying IDs\nwith a pen and paper.',
      sub: "IDGate gives your housing society Aadhaar-verified entry records for every visitor, domestic worker, and delivery person — with a log that residents can actually trust.",
    },
    painPoints: {
      h2: "What residents ask that your security team can't answer",
      sub: "These are the questions that lose residents' trust.",
      items: [
        'Who is this domestic worker — have we verified their identity?',
        'Can you tell me everyone who entered Block B last Thursday?',
        'A delivery person entered without being logged — who was it?',
        "Our maid was seen in a neighbour's flat — do you have a record?",
        'What happens if an unverified visitor enters our building?',
        'Is the visitor data we collect DPDP compliant?',
      ],
    },
    steps: [
      {
        title: 'Visitor scans QR at gate',
        body: 'One QR code at your society gate. Works on any smartphone with mAadhaar installed. Security guard does not touch visitor IDs.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Identity confirmed in under 60 seconds by UIDAI. No paper logbook. No manual entry. Timestamp and consent captured automatically.',
      },
      {
        title: 'Record synced to your society management app',
        body: 'Verified entry record sent to your society management system or Google Sheet. Residents can request the log at any time.',
      },
    ],
    benefits: [
      'Aadhaar-verified entry for every visitor and domestic worker',
      'Real-time entry log that residents can request',
      'DPDP compliant — consent captured at every check-in',
      'Domestic worker onboarding with verified identity',
      'Security staff do not handle visitor IDs',
      'Society management app or Google Sheet integration',
      'Separate flows for visitors, domestic workers, and deliveries',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from RWA committees and facility managers',
      items: [
        {
          q: 'Can IDGate verify domestic workers for ongoing entry passes?',
          a: "Yes. IDGate supports onboarding flows where a domestic worker is Aadhaar-verified once and added to an approved list. Subsequent entries are logged against their verified identity without re-verification each time.",
        },
        {
          q: 'How does IDGate help if there is a security incident in our society?',
          a: 'Every IDGate check-in creates an immutable, timestamped, Aadhaar-verified entry record. If an incident occurs, you can export the full entry log for any date range in under 30 seconds for reporting to police or your RWA committee.',
        },
        {
          q: 'Can residents get notified when a visitor enters?',
          a: 'IDGate can trigger a webhook event on each verified entry. Your society management app (MyGate, NoBrokerHood, Apna Complex) can use this webhook to send resident notifications when their visitor checks in.',
        },
      ],
    },
    onlineUses: [
      'Send a pre-approval link to expected visitors — guard verifies a pre-cleared identity, not a paper slip',
      'Embed in your society management app for domestic worker onboarding before their first day',
      'Add to your delivery pre-authorization form so residents approve verified couriers in advance',
    ],
    cta: {
      h2: 'Give your residents the security they expect.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your society',
    },
  },

  'jan-seva-kendra': {
    path: '/jan-seva-kendra',
    slug: 'jan_seva_kendra',
    seo: {
      title: 'Aadhaar Check-in for Govt Offices & Jan Seva Kendras',
      description:
        'Verify citizen identity at government service centres with Aadhaar-verified digital check-in. Audit-ready, DPDP compliant. Free pilot for Jan Seva Kendras and govt offices in India.',
      keywords:
        'Jan Seva Kendra visitor management, government service centre check-in India, citizen verification Aadhaar, govt office visitor log India, Common Service Centre KYC',
      url: 'https://idgate.in/jan-seva-kendra',
    },
    structuredDataName: 'IDGate — Govt Office & Jan Seva Kendra Check-in',
    structuredDataDescription:
      "Verify citizen identity at government service centres and Jan Seva Kendras with Aadhaar-verified digital check-in. Audit-ready, DPDP compliant.",
    hero: {
      eyebrow: 'For Govt Offices & Jan Seva Kendras',
      h1: 'Citizen service delivery starts\nwith knowing who you\'re serving.',
      sub: "IDGate gives Jan Seva Kendras and government offices an Aadhaar-verified service record for every citizen — eliminating fake beneficiary claims and giving auditors the trail they need.",
    },
    painPoints: {
      h2: 'What fails without verified citizen identity',
      sub: 'These gaps invite fraud and fail audit inspections.',
      items: [
        'Fake beneficiaries claiming government scheme benefits',
        'No way to verify if the person collecting a document is the applicant',
        'Paper service registers that get lost or tampered with',
        'No audit trail for scheme delivery inspections',
        'Citizens providing wrong Aadhaar numbers for service access',
        'No consent trail for citizen data collected under DPDP',
      ],
    },
    steps: [
      {
        title: 'Citizen scans QR at the counter',
        body: 'One QR code at your service window or waiting area. Works on any smartphone with mAadhaar installed.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Citizen identity confirmed in under 60 seconds directly by UIDAI. No paper form. No manual check. Consent captured digitally.',
      },
      {
        title: 'Service record created automatically',
        body: 'Verified service record logged in your government MIS or Google Sheet via webhook. Audit-ready for scheme inspection officers.',
      },
    ],
    benefits: [
      'Aadhaar-verified identity for every citizen interaction',
      'Fake beneficiary detection at the point of service',
      'Audit-ready service delivery records',
      'DPDP compliant — consent captured at every interaction',
      'Government MIS or Google Sheet integration via webhook',
      'No staff handling citizen Aadhaar documents',
      'Exportable service log for scheme audits and inspections',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from Jan Seva Kendra operators and government officers',
      items: [
        {
          q: 'Can IDGate be used at Common Service Centres (CSCs) for Aadhaar-based services?',
          a: "Yes. IDGate integrates with UIDAI's Aadhaar authentication infrastructure. It is designed for regulated government service environments where citizen identity must be verified before service delivery.",
        },
        {
          q: 'How does IDGate prevent fake beneficiary claims at Jan Seva Kendras?',
          a: "IDGate verifies each citizen's identity directly with UIDAI before the service interaction is recorded. A fake beneficiary cannot claim services under someone else's name because verification requires the actual person with their mAadhaar.",
        },
        {
          q: 'Can IDGate records be used during government scheme audits?',
          a: 'Yes. Every IDGate interaction creates a timestamped, Aadhaar-verified service record. These records are exportable for any date range and can be provided to scheme audit officers as proof of verified citizen interactions.',
        },
      ],
    },
    onlineUses: [
      'Send a pre-registration link via WhatsApp so citizens complete identity verification before visiting',
      'Embed in your citizen portal for scheme application identity verification online',
      'Add to your appointment booking form to eliminate fake beneficiary walk-ins',
    ],
    cta: {
      h2: 'Make every citizen interaction audit-proof.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your centre',
    },
  },

  gyms: {
    path: '/gyms',
    slug: 'gyms',
    seo: {
      title: 'Aadhaar Member Verification for Gyms & Fitness Centres',
      description:
        'Stop proxy memberships and revenue leakage at your gym with Aadhaar-verified member check-in. DPDP compliant. Free pilot for gyms and fitness centres in India.',
      keywords:
        'gym member verification India, fitness centre check-in Aadhaar, gym membership KYC India, proxy membership prevention gym, digital gym check-in India',
      url: 'https://idgate.in/gyms',
    },
    structuredDataName: 'IDGate — Gym & Fitness Centre Member Verification',
    structuredDataDescription:
      'Stop proxy memberships and revenue leakage at gyms with Aadhaar-verified member check-in. DPDP compliant.',
    hero: {
      eyebrow: 'For Gyms & Fitness Centres',
      h1: 'Proxy members and expired passes\nare bleeding your revenue.',
      sub: 'IDGate ties every check-in to a verified Aadhaar identity — so memberships cannot be shared, expired passes cannot be used, and you know exactly who is on your floor.',
    },
    painPoints: {
      h2: 'What gym owners lose to unverified check-in',
      sub: 'These are recurring revenue leaks that compound every month.',
      items: [
        'Members sharing passes with non-paying friends',
        'Expired memberships still accessing the facility',
        'Staff unable to challenge a familiar face at the turnstile',
        'No KYC at onboarding — wrong name, wrong phone number',
        'No way to produce a daily attendance log for insurance audits',
        'No consent trail for member data collected under DPDP',
      ],
    },
    steps: [
      {
        title: 'Member scans QR at the entrance',
        body: 'One QR code at your gym entrance. Works on any smartphone with mAadhaar installed. Your staff does not need to check IDs.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Member identity confirmed in under 60 seconds by UIDAI. Tied to their active membership. No proxy possible.',
      },
      {
        title: 'Attendance record synced to your gym software',
        body: 'Verified check-in logged in your gym management system or Google Sheet. Daily attendance exportable anytime.',
      },
    ],
    benefits: [
      'Aadhaar-verified check-in — zero proxy memberships',
      'Expired membership access automatically blocked',
      'KYC at onboarding — verified name and demographic',
      'DPDP compliant — consent captured at every check-in',
      'Daily attendance log exportable for insurance and audits',
      'Gym management system or Google Sheet integration',
      'Staff do not need to challenge members at the door',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from gym owners and fitness centre managers',
      items: [
        {
          q: 'How does IDGate stop proxy memberships at a gym?',
          a: "Each check-in requires the member to verify their identity with their own mAadhaar app. Since biometric or OTP-based Aadhaar authentication cannot be proxied to another person, shared passes become impossible.",
        },
        {
          q: 'Can IDGate be used for member onboarding as well as daily check-in?',
          a: "Yes. IDGate supports both a one-time onboarding flow (where the member's identity is captured and linked to their membership) and a daily check-in flow (where identity is verified on each visit).",
        },
        {
          q: 'Does IDGate work with our existing gym management software?',
          a: 'IDGate delivers verified check-in records via webhook or to a Google Sheet. Most gym management platforms (Arbox, Mindbody, Gymmaster) can receive webhook data or import from Sheets.',
        },
      ],
    },
    onlineUses: [
      'Send a membership enrollment link before the first visit — identity verified before they walk in',
      'Embed in your gym website\'s join-now page for fully online, verified member onboarding',
      'Add to your WhatsApp welcome message so new members are KYC-ready from day one',
    ],
    cta: {
      h2: 'Stop proxy memberships this month.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your gym',
    },
  },

  coaching: {
    path: '/coaching',
    slug: 'coaching',
    seo: {
      title: 'Aadhaar Student Verification for Coaching Institutes',
      description:
        "Verify every student's identity at enrolment and attendance with Aadhaar-verified digital check-in. DPDP compliant. Free pilot for coaching institutes in India.",
      keywords:
        'coaching institute student verification India, Aadhaar coaching enrolment, student KYC India, coaching centre check-in, exam eligibility verification India',
      url: 'https://idgate.in/coaching',
    },
    structuredDataName: 'IDGate — Coaching Institute Student Verification',
    structuredDataDescription:
      'Verify every student identity at coaching institutes with Aadhaar-verified digital enrolment and attendance. DPDP compliant.',
    hero: {
      eyebrow: 'For Coaching Institutes',
      h1: 'Student identity at enrolment\nis your protection in every dispute.',
      sub: 'IDGate gives coaching institutes Aadhaar-verified student records — so exam eligibility, attendance, and fee receipts are always backed by a verified identity that holds up in court.',
    },
    painPoints: {
      h2: 'What coaching institutes lose without verified identity',
      sub: 'Each of these becomes a legal or operational crisis.',
      items: [
        "Wrong student attending classes under another's name",
        'Fee disputes with no verified name on the receipt',
        'Exam eligibility challenged when attendance record is unverified',
        "Parents disputing attendance records at the end of the batch",
        'No DPDP-compliant consent for student data collected',
        'Batch register with nicknames and wrong DOBs',
      ],
    },
    steps: [
      {
        title: 'Student scans QR at enrolment or entry',
        body: 'One QR code at your reception or classroom door. Works on any smartphone with mAadhaar installed. No app for staff.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Student identity confirmed in under 60 seconds by UIDAI. Legal name, DOB, and consent captured digitally at the moment of enrolment.',
      },
      {
        title: 'Enrolment or attendance record synced to your system',
        body: 'Verified student record sent to your institute management system or Google Sheet. Exportable for exam bodies and dispute resolution.',
      },
    ],
    benefits: [
      'Aadhaar-verified student identity at enrolment',
      'Attendance tied to verified identity — no proxy attendance',
      'Exam eligibility backed by a UIDAI-verified audit trail',
      'Fee receipts linked to verified legal name',
      'DPDP compliant — consent captured at every check-in',
      'Parent dispute resolution with verifiable attendance records',
      'Google Sheet or institute management system integration',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from coaching institute owners',
      items: [
        {
          q: 'Can IDGate handle both enrolment KYC and daily attendance for students?',
          a: "Yes. IDGate supports two separate QR flows: one for one-time enrolment verification (where the student's legal identity is captured) and one for daily attendance (where identity is re-verified on each class entry).",
        },
        {
          q: 'How does IDGate help with exam eligibility disputes?',
          a: 'IDGate generates an attendance log where each record is backed by a UIDAI-verified identity with timestamp and consent. This record can be provided to exam boards or parents as proof of verified attendance.',
        },
        {
          q: 'Does IDGate capture parent consent for minors?',
          a: 'For students who are minors, IDGate supports a guardian consent flow where the parent verifies their identity and provides consent on behalf of the student at enrolment. This satisfies DPDP requirements for processing minor data.',
        },
      ],
    },
    onlineUses: [
      'Send an enrollment verification link to students before the first class — legal name confirmed at enrolment',
      'Embed in your institute website\'s admission form for identity-verified online enrollment',
      'Add to your fee payment confirmation so every receipt is tied to a verified identity',
    ],
    cta: {
      h2: 'Make every student record dispute-proof.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your institute',
    },
  },

  schools: {
    path: '/schools',
    slug: 'schools',
    seo: {
      title: 'Aadhaar Visitor & Student Verification for Schools & Colleges',
      description:
        'Secure your campus with Aadhaar-verified visitor and student check-in. DPDP compliant, exam-hall ready. Free pilot for schools and colleges in India.',
      keywords:
        'school visitor management India, college student verification, campus security check-in Aadhaar, school gate management India, parent verification school, exam hall attendance India',
      url: 'https://idgate.in/schools',
    },
    structuredDataName: 'IDGate — School & College Visitor Management',
    structuredDataDescription:
      'Secure school and college campuses with Aadhaar-verified visitor and student check-in. DPDP compliant, exam-hall ready.',
    hero: {
      eyebrow: 'For Schools & Colleges',
      h1: 'Campus security starts at the gate,\nnot the classroom.',
      sub: "IDGate gives schools and colleges Aadhaar-verified entry records for visitors, parents, and exam hall attendees — with a digital audit trail that CBSE and university inspections accept.",
    },
    painPoints: {
      h2: 'What breaks with manual campus access control',
      sub: 'Each of these creates a safeguarding or compliance risk.',
      items: [
        'Unknown visitors entering campus without identity verification',
        'Parent pickup without verifying the person collecting the student',
        'Exam hall attendance with wrong student or proxy candidate',
        'No log of who entered the campus on a given day',
        'Security staff manually checking IDs inconsistently',
        'No DPDP-compliant consent for visitor data collected',
      ],
    },
    steps: [
      {
        title: 'Visitor or parent scans QR at the gate',
        body: 'One QR code at your school or college gate. Works on any smartphone with mAadhaar installed. Guard does not need to handle IDs.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Identity confirmed in under 60 seconds by UIDAI. Timestamp and consent captured automatically for every entry.',
      },
      {
        title: 'Entry record synced to your school management system',
        body: 'Verified visitor log delivered to your school ERP or Google Sheet. Exportable for inspection officers and safeguarding audits.',
      },
    ],
    benefits: [
      'Aadhaar-verified entry for every campus visitor',
      'Parent identity verified before student handover',
      'Exam hall attendance backed by UIDAI-verified record',
      'DPDP compliant — consent captured at every check-in',
      'Exportable campus entry log for CBSE and university inspections',
      'Security staff do not handle visitor ID documents',
      'School ERP or Google Sheet integration via webhook',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from school principals and college administrators',
      items: [
        {
          q: 'Can IDGate be used to verify parents at the school gate for student pickup?',
          a: "Yes. IDGate supports a parent verification flow where the parent verifies their Aadhaar identity at the gate before a student is handed over. The record includes the parent's verified identity and timestamp, creating an audit trail for safeguarding.",
        },
        {
          q: 'Can IDGate be used for exam hall attendance verification?',
          a: "Yes. IDGate supports exam check-in flows where each student's identity is verified against UIDAI before entering the exam hall. The verified attendance record can be submitted to CBSE, universities, or exam boards as proof.",
        },
        {
          q: 'How does IDGate handle DPDP compliance for minor student data?',
          a: 'For minor students, IDGate does not collect student Aadhaar data directly. Visitor and parent verification flows collect only adult identity data. For students who are 18+, full Aadhaar verification applies with their consent.',
        },
      ],
    },
    onlineUses: [
      'Send a parent verification link before student pickup — identity confirmed before the gate opens',
      'Embed in your online admission form so applicant identity is verified at the point of application',
      'Add to your exam hall pre-registration portal for UIDAI-verified candidate check-in',
    ],
    cta: {
      h2: 'Make your campus safer this term.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your school',
    },
  },

  hostels: {
    path: '/hostels',
    slug: 'hostels',
    seo: {
      title: 'Aadhaar Tenant Verification for Hostels & PG Accommodations',
      description:
        'Replace manual tenant ID checks with Aadhaar-verified digital onboarding for hostels and PGs. Police-compliant. Free pilot for hostel and PG operators in India.',
      keywords:
        'PG tenant verification India, hostel check-in Aadhaar, paying guest registration India, hostel police compliance India, tenant KYC India, PG owner compliance',
      url: 'https://idgate.in/hostels',
    },
    structuredDataName: 'IDGate — Hostel & PG Tenant Verification',
    structuredDataDescription:
      'Replace manual tenant ID checks at hostels and PGs with Aadhaar-verified digital onboarding. Police-compliant, DPDP compliant.',
    hero: {
      eyebrow: 'For Hostels & PG Accommodations',
      h1: 'Police compliance for your PG\nstarts with knowing your tenants.',
      sub: "IDGate gives hostel and PG operators Aadhaar-verified tenant records — satisfying police commissioner registration requirements and protecting you from liability when an unverified tenant causes problems.",
    },
    painPoints: {
      h2: 'What hostel and PG operators get wrong',
      sub: 'Police inspections and disputes expose all of these.',
      items: [
        'Tenants registered with fake IDs or incorrect names',
        'No police commissioner compliant tenant register',
        'Sub-tenants living in the accommodation without verification',
        'No record of when a tenant checked in or checked out',
        'Photocopied Aadhaar cards in a drawer — a DPDP violation',
        'No way to export tenant records for a police inspection',
      ],
    },
    steps: [
      {
        title: 'Tenant scans QR at check-in',
        body: 'One QR code at your reception desk. Works on any smartphone with mAadhaar installed. You do not need to photocopy any ID.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Tenant identity confirmed in under 60 seconds by UIDAI. Legal name, address, and consent captured digitally. No physical documents handled.',
      },
      {
        title: 'Tenant record synced to your register',
        body: 'Verified tenant record delivered to your property management system or Google Sheet. Exportable for police inspections at any time.',
      },
    ],
    benefits: [
      'Aadhaar-verified tenant identity at check-in',
      'Police commissioner compliant digital tenant register',
      'No photocopied Aadhaar cards — DPDP compliant',
      'Sub-tenant and guest verification with separate QR flows',
      'Check-in and check-out timestamps automatically recorded',
      'Exportable tenant log for police inspections in under 30 seconds',
      'Google Sheet or property management system integration',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from hostel and PG operators',
      items: [
        {
          q: "Does IDGate's tenant record satisfy police commissioner registration requirements?",
          a: "IDGate generates a verified digital record with the tenant's legal name, address, Aadhaar verification status, and timestamped consent — equivalent to the details required for police commissioner tenant registration in most Indian states.",
        },
        {
          q: 'What happens to stored Aadhaar data under DPDP?',
          a: 'IDGate does not store Aadhaar numbers. The verification is handled by UIDAI and IDGate records only the verified output (name, address, timestamp, consent). You are DPDP compliant because no raw Aadhaar data is collected or stored.',
        },
        {
          q: 'Can IDGate handle guest verification for hostel visitors?',
          a: "Yes. IDGate supports separate QR flows for tenant check-in and visitor check-in. Visitors to a tenant's room can be verified through the visitor flow and the record linked to the relevant tenant.",
        },
      ],
    },
    onlineUses: [
      'Send a tenant verification link with your booking confirmation — identity verified before they move in',
      'Embed in your PG listing page so prospective tenants complete KYC at the time of inquiry',
      'Add to your rental agreement digital signing flow for a fully verified, police-compliant record',
    ],
    cta: {
      h2: 'Pass your next police inspection with confidence.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your hostel',
    },
  },

  retail: {
    path: '/retail',
    slug: 'retail',
    seo: {
      title: 'Aadhaar Verification for Retail Outlets & Shopping Malls',
      description:
        'Verify contractors, staff, and VIP customers at retail outlets and malls with Aadhaar-verified digital check-in. DPDP compliant. Free pilot in India.',
      keywords:
        'mall visitor management India, retail employee check-in, loyalty program KYC India, shopping mall contractor verification, retail staff onboarding India',
      url: 'https://idgate.in/retail',
    },
    structuredDataName: 'IDGate — Retail & Mall Visitor Management',
    structuredDataDescription:
      'Verify contractors, staff, and VIP customers at retail outlets and malls with Aadhaar-verified digital check-in. DPDP compliant.',
    hero: {
      eyebrow: 'For Retail & Shopping Malls',
      h1: "Know exactly who's on your floor —\ncontractors, staff, and VIPs.",
      sub: 'IDGate gives retail outlets and malls Aadhaar-verified entry records for contractors, temp staff, and VIP loyalty members — with a log that holds up in theft investigations and brand audits.',
    },
    painPoints: {
      h2: 'What retail security gaps cost you',
      sub: 'Brand audits and theft investigations surface all of these.',
      items: [
        'Unverified contractors accessing restricted areas',
        'Temp staff onboarded without proper identity verification',
        'Loyalty programme with fake customer identities',
        'No entry log for contractor activity during off-hours',
        'VIP customer data collected without DPDP-compliant consent',
        'Post-theft investigation with no verified entry records',
      ],
    },
    steps: [
      {
        title: 'Contractor, staff, or VIP scans QR at entry',
        body: 'One QR code at your service entrance or VIP desk. Works on any smartphone with mAadhaar installed. Security staff do not need to handle IDs.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Identity confirmed in under 60 seconds by UIDAI. Entry purpose, timestamp, and consent captured automatically.',
      },
      {
        title: 'Entry record synced to your retail management system',
        body: 'Verified entry record sent to your Google Sheet or retail operations platform. Exportable for brand audits and theft investigations.',
      },
    ],
    benefits: [
      'Aadhaar-verified entry for contractors and temp staff',
      'VIP loyalty member identity verification at onboarding',
      'Post-theft investigation log with verified entry records',
      'DPDP compliant — consent captured at every check-in',
      'Separate flows for contractors, staff, and VIP customers',
      'Retail management system or Google Sheet integration',
      'Brand audit-ready contractor compliance log',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from retail operations and mall management teams',
      items: [
        {
          q: 'Can IDGate verify loyalty programme members at onboarding?',
          a: "Yes. IDGate supports a customer onboarding flow where loyalty programme members verify their Aadhaar identity once. Subsequent visits can reference the verified identity without re-verification.",
        },
        {
          q: 'How does IDGate help with contractor compliance in a mall environment?',
          a: 'IDGate creates a timestamped, Aadhaar-verified record for every contractor entering the premises. Brand compliance and mall management teams can export contractor logs for any date range for audits.',
        },
        {
          q: 'Can IDGate be used at multiple retail outlets across a chain?',
          a: 'Yes. IDGate supports multi-location deployment. Each outlet gets its own QR code but all records flow to a single Google Sheet or webhook endpoint, giving you a unified verified entry log across the chain.',
        },
      ],
    },
    onlineUses: [
      'Send an onboarding link to contractors before their first site visit — identity verified before access is granted',
      'Embed in your loyalty programme registration page for verified member enrollment at sign-up',
      'Add to your VIP event invitation so guest identity is confirmed before the day',
    ],
    cta: {
      h2: 'Make your store floor verifiably secure.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your outlet',
    },
  },

  'religious-trusts': {
    path: '/religious-trusts',
    slug: 'religious_trusts',
    seo: {
      title: 'Aadhaar Devotee Registration for Temples & Religious Trusts',
      description:
        'Register and verify devotees at temple events and religious trusts with Aadhaar-verified digital check-in. DPDP compliant. Free pilot for temples and religious institutions in India.',
      keywords:
        'temple devotee registration India, religious trust visitor management, temple event check-in Aadhaar, temple crowd management India, religious institution KYC India',
      url: 'https://idgate.in/religious-trusts',
    },
    structuredDataName: 'IDGate — Temple & Religious Trust Devotee Registration',
    structuredDataDescription:
      'Register and verify devotees at temples and religious trusts with Aadhaar-verified digital check-in. DPDP compliant, audit-ready.',
    hero: {
      eyebrow: 'For Temples & Religious Trusts',
      h1: 'Devotee registration at scale\nneeds more than a paper list.',
      sub: 'IDGate gives temples and religious trusts Aadhaar-verified devotee records for major events, trust membership, and donation attribution — with a digital register that withstands trust audits.',
    },
    painPoints: {
      h2: 'What temples face without verified registration',
      sub: 'These become crises during major events and trust audits.',
      items: [
        'No verified headcount during major festival events',
        'Donation records attributed to unverified identities',
        'Trust membership with fake names and incorrect addresses',
        'No crowd management data for safety compliance',
        'Post-incident investigation with no verified entry records',
        'No DPDP-compliant consent for devotee data collected',
      ],
    },
    steps: [
      {
        title: 'Devotee scans QR at entry or registration',
        body: 'One QR code at your temple gate or registration desk. Works on any smartphone with mAadhaar installed. Temple staff do not handle devotee IDs.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Devotee identity confirmed in under 60 seconds by UIDAI. Name, timestamp, and consent captured digitally for every registration.',
      },
      {
        title: 'Devotee record synced to your trust management system',
        body: 'Verified registration record sent to your trust management system or Google Sheet. Exportable for trust audits and safety inspections.',
      },
    ],
    benefits: [
      'Aadhaar-verified devotee registration for all events',
      'Verified donation attribution — no disputes on record',
      'Trust membership tied to verified legal identities',
      'DPDP compliant — consent captured at every registration',
      'Real-time crowd count for safety compliance',
      'Trust management system or Google Sheet integration',
      'Post-incident investigation with verified entry records',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from temple trustees and religious trust administrators',
      items: [
        {
          q: 'Can IDGate be used for large-scale festival crowd management at temples?',
          a: 'Yes. IDGate supports multi-point QR deployment — multiple entry gates each with their own QR code, all logging to the same Google Sheet or trust management system. This gives real-time headcount across all entry points.',
        },
        {
          q: 'How does IDGate help with verified donation attribution?',
          a: "IDGate can be used at the donation counter as a verification step before the donation is recorded. The donation record is linked to the donor's verified Aadhaar identity, eliminating disputes over who made a donation.",
        },
        {
          q: 'Is collecting devotee Aadhaar data DPDP compliant for religious institutions?',
          a: "IDGate does not store Aadhaar numbers. It captures only the verified output (name, timestamp, consent). Devotees provide explicit digital consent before verification. Religious trusts using IDGate satisfy DPDP data minimisation and consent requirements.",
        },
      ],
    },
    onlineUses: [
      'Send a seva booking verification link via WhatsApp — devotees arrive identity-confirmed',
      'Embed in your online donation form so every contribution is tied to a verified donor identity',
      'Add to your trust membership registration portal for verified, audit-ready member records',
    ],
    cta: {
      h2: 'Make your temple events and trust records verifiable.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your trust',
    },
  },

  logistics: {
    path: '/logistics',
    slug: 'logistics',
    seo: {
      title: 'Aadhaar Gate Pass Management for Logistics Parks & Warehouses',
      description:
        'Replace manual gate passes with Aadhaar-verified digital check-in for logistics parks and warehouses. DPDP compliant, theft-investigation ready. Free pilot in India.',
      keywords:
        'warehouse visitor management India, logistics gate pass Aadhaar, contractor check-in India, warehouse security check-in, logistics park access control India',
      url: 'https://idgate.in/logistics',
    },
    structuredDataName: 'IDGate — Logistics Park & Warehouse Gate Pass Management',
    structuredDataDescription:
      'Replace manual gate passes with Aadhaar-verified digital check-in for logistics parks and warehouses. DPDP compliant, theft-investigation ready.',
    hero: {
      eyebrow: 'For Logistics Parks & Warehouses',
      h1: 'Every contractor entering your warehouse\nshould be who they say they are.',
      sub: "IDGate replaces paper gate passes with Aadhaar-verified digital entry records — so contract labour is verified at onboarding, every entry is logged, and theft investigations have real answers.",
    },
    painPoints: {
      h2: 'What warehouse security gaps cost you',
      sub: 'These gaps are exploited in every major warehouse theft.',
      items: [
        'Contract labour entering with unverified identity',
        'Paper gate passes that are forged or transferred',
        'No searchable entry log for theft investigation',
        'Daily labour onboarded with wrong names and phone numbers',
        'Vendor representatives accessing restricted areas without verification',
        'No DPDP-compliant consent for worker data collected',
      ],
    },
    steps: [
      {
        title: 'Contractor or worker scans QR at the gate',
        body: 'One QR code at your warehouse gate. Works on any smartphone with mAadhaar installed. Gate security staff do not need to handle ID documents.',
      },
      {
        title: 'UIDAI verifies identity instantly',
        body: 'Identity confirmed in under 60 seconds by UIDAI. Entry purpose, timestamp, and consent captured automatically. Forged passes are impossible.',
      },
      {
        title: 'Gate record synced to your WMS or Google Sheet',
        body: 'Verified gate entry logged in your Warehouse Management System or Google Sheet. Exportable for theft investigations and compliance audits.',
      },
    ],
    benefits: [
      'Aadhaar-verified entry for every contractor and daily worker',
      'Forged gate passes eliminated — identity verified by UIDAI',
      'Searchable entry log for theft investigation and insurance claims',
      'DPDP compliant — consent captured at every entry',
      'Contract labour onboarding with verified identity',
      'WMS or Google Sheet integration via webhook',
      'Exportable gate log for any date range in under 30 seconds',
      'Free 30-day pilot — no contract, no card',
    ],
    faq: {
      h2: 'Common questions from warehouse and logistics park security managers',
      items: [
        {
          q: 'Can IDGate be used for daily contract labour onboarding at a warehouse?',
          a: "Yes. IDGate supports a daily labour onboarding flow where each contract worker verifies their Aadhaar identity before entering the warehouse. Repeat workers are recognised from their previous verified record, speeding up the process.",
        },
        {
          q: 'How does IDGate support theft investigations at warehouses?',
          a: 'Every IDGate entry creates a timestamped, Aadhaar-verified gate record. In the event of a theft, the complete entry log for the relevant shift can be exported in under 30 seconds and provided to police or your insurance company.',
        },
        {
          q: 'Can IDGate manage separate access for vendors, contractors, and daily workers?',
          a: 'Yes. IDGate supports multiple QR flows — each for a different entry type. Vendor representatives, long-term contractors, and daily workers each get a separate verification flow, all logged to the same unified WMS or Google Sheet.',
        },
      ],
    },
    onlineUses: [
      'Send a pre-entry verification link to contractors before the shift — identity confirmed before the gate opens',
      'Embed in your vendor portal so supplier representatives complete identity verification at onboarding',
      'Add to your daily labour WhatsApp group so workers are verified before reaching the gate',
    ],
    cta: {
      h2: 'Make every warehouse entry traceable.',
      sub: 'Free 30-day pilot. One afternoon to set up. No contract.',
      btn: 'Start free pilot for your facility',
    },
  },
};
